import type { StateCreator } from 'zustand/vanilla';
import type {
  AgentClipGroup,
  CanvasDetailType,
  NodeBusinessData,
  NodeData,
  VideoClip,
  ViduContainerType as ViduV1Container,
} from '@/app/types';
import { containerToBusinessData } from '@/app/utils';

/**
 * Store 状态类型
 */
interface AgentCanvasStoreState {
  /** ============================== 画布管理 ============================== */
  /** 画布 ID */
  agentCanvasDetail: CanvasDetailType | null;

  /** ============================== 节点管理 ============================== */
  /** 节点业务数据（不包含位置等 UI 状态） */
  agentNodeBusinessDataMap: Map<string, NodeBusinessData>;
  /** 正在加载中的节点 */
  agentLoadingNodeIds: string[];
  /** 是否正在轮询 */
  agentIsPolling: boolean;
  /** 轮询定时器 ID */
  agentPollingTimer: NodeJS.Timeout | null;

  /** ============================== 分镜脚本编辑对话框管理 ============================== */
  /** 是否打开分镜脚本编辑对话框 */
  agentIsOpenEditInClipDialog: boolean;
  /** 传入分镜脚本编辑的数据 */
  agentEditInClipPropsData: {
    videoGroupId: string;
    videoNodeData: NodeData;
  } | null;
  /** 分镜脚本中loading的节点 */
  agentEditInClipLoadingNodes: NodeData[];
  /** 视频片段数据 */
  agentClips: Map<string, VideoClip>;
  /** 视频片段组元数据 */
  agentClipGroups: AgentClipGroup[];
}

/**
 * Store Actions 类型
 */
export interface AgentCanvasStore extends AgentCanvasStoreState {
  /** ============================== 画布管理 ============================== */
  /** 设置画布详情 */
  setAgentCanvasDetail: (canvasDetail: CanvasDetailType | null) => void;
  /** 从容器列表初始化节点 */
  initializeAgentFromContainers: (containers: ViduV1Container[]) => void;
  /** 设置正在加载中的节点 */
  setAgentLoadingNodeIds: (nodeIds: string[]) => void;
  /** 添加loading节点 */
  addAgentLoadingNode: (nodeId: string) => void;
  /** 根据容器 ID 更新节点 */
  updateAgentNodeByContainer: (container: ViduV1Container) => void;
  /** 添加新节点（只添加业务数据） */
  addAgentNode: (container: ViduV1Container) => void;
  /** 设置轮询状态 */
  setAgentPollingState: (isPolling: boolean, timer: NodeJS.Timeout | null) => void;
  /** 清除轮询定时器 */
  clearAgentPollingTimer: () => void;
  /** 批量更新节点 */
  batchUpdateAgentContainers: (containers: ViduV1Container[]) => string[];
  /** 判断是否有AI创作的视频节点 */
  hasAgentAiCreationVideoNode: () => boolean;
  /** 关闭agent画布 */
  closeAgentCanvas: () => void;

  /** ============================== 分镜脚本编辑对话框管理 ============================== */
  // 分镜脚本编辑对话框管理
  openAgentEditInClipDialog: (videoGroupId: string, videoNodeData: NodeData) => void;
  /** 关闭分镜脚本编辑对话框 */
  closeAgentEditInClipDialog: () => void;
  /** 设置分镜脚本中loading的节点列表 */
  setAgentEditInClipLoadingNodes: (nodes: NodeData[]) => void;
  /** 移除分镜脚本中loading的节点 */
  removeAgentEditInClipLoadingNode: (nodeId: string) => void;
  /** 清空分镜脚本中loading的节点列表 */
  clearAgentEditInClipLoadingNodes: () => void;
  /** 获取某个组的所有 clips */
  getAgentClipsByGroup: (groupIndex: number) => VideoClip[];
  /** 获取某个组的当前 clip */
  getAgentCurrentClip: (groupIndex: number) => VideoClip | undefined;
  /** 初始化 clips 数据 */
  initializeAgentClips: (
    clipGroups: {
      clipGroupIndex: number;
      currentClipId: string;
      clipList: VideoClip[];
    }[],
  ) => void;
  /** 更新或插入 clip */
  updateAgentClip: (clip: VideoClip, groupIndex?: number) => void;
  /** 更新组属性 */
  updateAgentClipGroup: (groupIndex: number, updates: Partial<AgentClipGroup>) => void;
}

/**
 * 初始状态
 */
const initialState: AgentCanvasStoreState = {
  agentCanvasDetail: null,
  agentNodeBusinessDataMap: new Map(),
  agentLoadingNodeIds: [],
  agentIsPolling: false,
  agentPollingTimer: null,
  agentIsOpenEditInClipDialog: false,
  agentEditInClipPropsData: null,
  agentEditInClipLoadingNodes: [],
  agentClips: new Map(),
  agentClipGroups: [],
};

/**
 * Store Creator 实现
 */
export const createAgentCanvasSlice: StateCreator<AgentCanvasStore, [], [], AgentCanvasStore> = (set, get) => ({
  ...initialState,

  setAgentCanvasDetail: (canvasDetail: CanvasDetailType | null) => {
    set({ agentCanvasDetail: canvasDetail });
  },

  initializeAgentFromContainers: (containers: ViduV1Container[]) => {
    const { agentNodeBusinessDataMap } = get();

    if (!containers || containers.length === 0) {
      // 只有在当前有数据时才清空
      if (agentNodeBusinessDataMap.size > 0) {
        set({ agentNodeBusinessDataMap: new Map() });
      }
      return;
    }

    const businessDataList = containers.map((container) => {
      return containerToBusinessData(container);
    });

    const newMap = new Map<string, NodeBusinessData>();
    for (const data of businessDataList) {
      newMap.set(data.id, data);
    }

    // 检查是否有实际变化（比较大小和内容）
    if (agentNodeBusinessDataMap.size === newMap.size) {
      let hasChanged = false;
      const newMapEntries = Array.from(newMap.entries());
      for (const [id, newData] of newMapEntries) {
        const oldData = agentNodeBusinessDataMap.get(id);
        if (!oldData || JSON.stringify(oldData) !== JSON.stringify(newData)) {
          hasChanged = true;
          break;
        }
      }
      if (!hasChanged) {
        // 数据完全相同，不更新
        return;
      }
    }
    set({ agentNodeBusinessDataMap: newMap });
  },

  setAgentLoadingNodeIds: (nodeIds: string[]) => {
    const { agentLoadingNodeIds } = get();
    // 检查是否真的有变化
    if (
      agentLoadingNodeIds.length === nodeIds.length &&
      agentLoadingNodeIds.every((id, index) => id === nodeIds[index])
    ) {
      return; // 没有变化，不更新
    }
    set({ agentLoadingNodeIds: nodeIds });
  },
  // 添加loading节点
  addAgentLoadingNode: (nodeId: string) => {
    const { agentLoadingNodeIds } = get();
    set({ agentLoadingNodeIds: [...agentLoadingNodeIds, nodeId] });
  },

  updateAgentNodeByContainer: (container: ViduV1Container) => {
    const { agentNodeBusinessDataMap } = get();
    const containerId = container.id || '';
    const node = agentNodeBusinessDataMap.get(containerId);
    // 如果节点不存在，直接返回
    if (!node) {
      return;
    }

    // 构建数据节点
    const businessData = containerToBusinessData(container);

    // 检查数据是否真的变化了
    const hasChanged = JSON.stringify(node.data) !== JSON.stringify(businessData.data);
    if (!hasChanged) {
      return;
    }

    const newMap = new Map(agentNodeBusinessDataMap);
    newMap.set(containerId, businessData);
    set({ agentNodeBusinessDataMap: newMap });
  },

  addAgentNode: (container: ViduV1Container) => {
    const { agentNodeBusinessDataMap } = get();
    const containerId = container.id || '';

    // 检查节点是否已存在
    if (agentNodeBusinessDataMap.has(containerId)) {
      // 如果节点已存在，更新它
      get().updateAgentNodeByContainer(container);
      return;
    }

    const newNode = containerToBusinessData(container);

    const newMap = new Map(agentNodeBusinessDataMap);

    // 如果其是视频节点 && 父节点是视频组，则更新视频组的sub_ids
    if (newNode.type === 'creationVideoNode' && newNode.data.parent_ids && newNode.data.parent_ids.length > 0) {
      // 先找到父节点中是否有存在的视频组节点
      const parentVideoGroupNode = newNode.data.parent_ids.find((parentId) => {
        const parentNode = agentNodeBusinessDataMap.get(parentId);
        return parentNode && parentNode.data.container_type === 'video_group';
      });
      // 找到视频组节点后，更新视频组的sub_ids
      if (parentVideoGroupNode) {
        const parentVideoGroupNodeData = agentNodeBusinessDataMap.get(parentVideoGroupNode);
        if (parentVideoGroupNodeData) {
          parentVideoGroupNodeData.data.video_group?.sub_ids?.push(containerId);
          // 更新视频组节点
          newMap.set(parentVideoGroupNode, parentVideoGroupNodeData);
        }
      }
    }
    newMap.set(newNode.id, newNode);
    set({ agentNodeBusinessDataMap: newMap });
  },

  setAgentPollingState: (isPolling: boolean, timer: NodeJS.Timeout | null) => {
    set({ agentIsPolling: isPolling, agentPollingTimer: timer });
  },

  clearAgentPollingTimer: () => {
    const { agentPollingTimer } = get();
    if (agentPollingTimer) {
      clearTimeout(agentPollingTimer);
    }
    set({ agentIsPolling: false, agentPollingTimer: null });
  },

  batchUpdateAgentContainers: (containers: ViduV1Container[]): string[] => {
    const { agentNodeBusinessDataMap, addAgentNode, updateAgentNodeByContainer } = get();
    if (!containers || containers.length === 0) {
      return [];
    }

    const newContainerIds: string[] = [];

    // 遍历容器，智能判断新增还是更新
    for (const container of containers) {
      const containerId = container.id || '';
      if (agentNodeBusinessDataMap.has(containerId)) {
        // 已存在，更新
        updateAgentNodeByContainer(container);
      } else {
        // 新增
        addAgentNode(container);
        newContainerIds.push(containerId);
      }
    }

    return newContainerIds;
  },

  /** 判断是否有AI创作的视频节点 */
  hasAgentAiCreationVideoNode: () => {
    const { agentNodeBusinessDataMap, agentCanvasDetail } = get();
    /**
     * 判断是否存在比当前画布创建时间更晚的 AI 创作视频组节点
     * @returns 是否存在更晚创建的 AI 创作视频组节点
     */
    return Array.from(agentNodeBusinessDataMap.values()).some((node) => {
      if (
        node.type !== 'creationVideoNode' ||
        node.data.nodeType !== 'aiCreation' ||
        node.data.container_type !== 'video_group'
      ) {
        return false;
      }
      if (agentCanvasDetail?.created_at == null || node.data.created_at == null) {
        return false;
      }
      return new Date(node.data.created_at).getTime() > new Date(agentCanvasDetail.created_at).getTime();
    });
  },

  openAgentEditInClipDialog: (videoGroupId: string, videoNodeData: NodeData) => {
    set({ agentIsOpenEditInClipDialog: true, agentEditInClipPropsData: { videoGroupId, videoNodeData } });
  },
  closeAgentEditInClipDialog: () => {
    set({
      agentIsOpenEditInClipDialog: false,
      agentEditInClipPropsData: null,
      // 自动清理 clips 数据
      agentClips: new Map(),
      agentClipGroups: [],
    });
  },
  /** 设置分镜脚本中loading的节点列表 */
  setAgentEditInClipLoadingNodes: (nodes: NodeData[]) => {
    const nodeMap = new Map<string, NodeData>();
    for (const node of [...get().agentEditInClipLoadingNodes, ...nodes]) {
      if (node.container_id) {
        nodeMap.set(node.container_id, node);
      }
    }
    set({ agentEditInClipLoadingNodes: Array.from(nodeMap.values()) });
  },
  /** 移除分镜脚本中loading的节点 */
  removeAgentEditInClipLoadingNode: (nodeId: string) => {
    set({
      agentEditInClipLoadingNodes: get().agentEditInClipLoadingNodes.filter((node) => node.container_id !== nodeId),
    });
  },
  /** 清空分镜脚本中loading的节点列表 */
  clearAgentEditInClipLoadingNodes: () => {
    set({ agentEditInClipLoadingNodes: [] });
  },

  getAgentClipsByGroup: (groupIndex) => {
    const { agentClips, agentClipGroups } = get();
    const group = agentClipGroups.find((g) => g.clipGroupIndex === groupIndex);
    if (!group) return [];
    return group.clipIds.map((id) => agentClips.get(id)).filter((clip): clip is VideoClip => clip !== undefined);
  },

  getAgentCurrentClip: (groupIndex) => {
    const { agentClips, agentClipGroups } = get();
    const group = agentClipGroups.find((g) => g.clipGroupIndex === groupIndex);
    return group ? agentClips.get(group.currentClipId) : undefined;
  },

  initializeAgentClips: (rawClipGroups) => {
    const clips = new Map<string, VideoClip>();
    const clipGroups: AgentClipGroup[] = [];

    rawClipGroups.forEach((group) => {
      // biome-ignore lint/suspicious/useIterableCallbackReturn: populate the clips map for lookup only
      group.clipList.forEach((clip) => clips.set(clip.id, clip));
      clipGroups.push({
        clipGroupIndex: group.clipGroupIndex,
        currentClipId: group.currentClipId,
        isCurrentClipLoaded: false,
        clipIds: group.clipList.map((clip) => clip.id),
      });
    });

    set({ agentClips: clips, agentClipGroups: clipGroups });
  },

  updateAgentClip: (clip, groupIndex) => {
    const { agentClips, agentClipGroups } = get();
    const newClips = new Map(agentClips);
    const existingClip = newClips.get(clip.id);

    // 合并或新增 clip 数据
    newClips.set(clip.id, existingClip ? { ...existingClip, ...clip } : clip);

    // 如果是新 clip 且指定了组，添加到组的 clipIds
    if (!existingClip && groupIndex !== undefined) {
      const newClipGroups = agentClipGroups.map((group) =>
        group.clipGroupIndex === groupIndex ? { ...group, clipIds: [clip.id, ...group.clipIds] } : group,
      );
      set({ agentClips: newClips, agentClipGroups: newClipGroups });
    } else {
      set({ agentClips: newClips });
    }
  },

  updateAgentClipGroup: (groupIndex, updates) => {
    const { agentClipGroups } = get();
    set({
      agentClipGroups: agentClipGroups.map((group) =>
        group.clipGroupIndex === groupIndex ? { ...group, ...updates } : group,
      ),
    });
  },

  closeAgentCanvas: () => {
    set({ ...initialState });
  },
});
